'use client';

import { IReviewsData } from '@/api/types/product';
import { IGoodsDetail, IProductDetail } from '@/api/types/public';
import Breadcrumbs from '@/components/Breadcrumbs';
import CycleMsg from '@/components/CycleMsg/CycleMsg';
import FreeGift from '@/components/GoodsDetail/FreeGift';
import LenseAvailable from '@/components/GoodsDetail/LenseAvailable';
import ProductIncluded from '@/components/GoodsDetail/ProductIncluded';
import TopCarousel from '@/components/GoodsDetail/TopCarousel';
import { useStore } from '@/providers/app-store-provider';
import { colors } from '@/theme/zeelool.theme';
import { IS_DE } from '@/utils/constants';
import { genPrice } from '@/utils/price';
import dynamic from 'next/dynamic';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ActivityBanner from './ActivityBanner';
import BottomButton from './BottomButton';
import { IProductDetailsPageInteractionBtnClickParams } from './CustomerReviewsItem';
import PCCustomerReviewsLoadingSkeleton from './PCCustomerReviewsLoadingSkeleton';
import PcProductInfo from './ProductInfo/pc';

const ProductTab = dynamic(() => import('./ProductTab'), { ssr: true });
const CustomerReviewsList = dynamic(() => import('./CustomerReviewsList'), { ssr: true });
const CustomerReviewsListClientOnly = dynamic(() => import('./CustomerReviewsList'), {
  ssr: false,
  loading: () => <PCCustomerReviewsLoadingSkeleton />,
});
const GoodsScene = dynamic(() => import('./GoodsScene'), { ssr: true });
const GoodsSceneClientOnly = dynamic(() => import('./GoodsScene'), { ssr: false });
const ProductRichText = dynamic(() => import('./PcProductRichText'), { ssr: true });
const ProductRichTextClientOnly = dynamic(() => import('./PcProductRichText'), { ssr: false });
const GoodsFeatureIntro = dynamic(() => import('./GoodsFeatureIntro'), { ssr: true });
const GoodsFeatureIntroClientOnly = dynamic(() => import('./GoodsFeatureIntro'), { ssr: false });

// 滚动固定BottomButton组件
const StickyBottomButton: React.FC<{
  detail: any;
  bottomButtonClick: (type: number, btnName: string) => void;
  addCartLoading: boolean;
  frameOnlyLoading: boolean;
  selectLensLoading: boolean;
  SDProductDetailsPageBtnClick?: (btnName: string) => void;
  getGlassesFilter: string;
}> = ({
  detail,
  bottomButtonClick,
  addCartLoading,
  frameOnlyLoading,
  selectLensLoading,
  SDProductDetailsPageBtnClick,
  getGlassesFilter,
}) => {
  const bottomButtonRef = useRef<HTMLDivElement>(null);
  const [showFixedButton, setShowFixedButton] = useState(false);

  // 滚动检测
  const handleScroll = useCallback(() => {
    if (!bottomButtonRef.current) return;

    const rect = bottomButtonRef.current.getBoundingClientRect();
    const isOutOfView = rect.bottom < 100;

    setShowFixedButton(isOutOfView);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const renderBottomButton = () => {
    return (
      <BottomButton
        detail={detail}
        bottomButtonClick={bottomButtonClick}
        addCartLoading={addCartLoading}
        frameOnlyLoading={frameOnlyLoading}
        selectLensLoading={selectLensLoading}
        SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
        isFixed={showFixedButton}
        isWeb
        glassesFilter={getGlassesFilter}
      />
    );
  };
  return (
    <>
      {/* 原始位置的BottomButton - PC端样式 */}
      <div ref={bottomButtonRef} className="mb-8">
        {renderBottomButton()}
      </div>

      {/* 固定在底部的BottomButton - 移动端样式（保持原有的fixed定位） */}
      {showFixedButton && renderBottomButton()}
    </>
  );
};

interface PcGoodsDetailProps {
  detail: {
    product: IProductDetail;
    customer: IGoodsDetail['customer'];
    select_lens_config: IGoodsDetail['select_lens_config'];
  };
  reviewsData: IReviewsData;
  getGlassesFilter: string;
  // 加载状态
  addCartLoading: boolean;
  frameOnlyLoading: boolean;
  selectLensLoading: boolean;
  // 事件处理
  refreshPageData: () => void;
  handleShowSizeGuide: () => void;
  bottomButtonClick: (type: number, btnName: string) => void;
  breadcrumbItems: Array<{ label: string; href: string }>;
  SD_TryONBtn_Exp: (btn_name: string, item: any, sdEventName: string) => void;
  SDProductDetailsPageBtnClick?: (btnName: string) => void;
  SDProductDetailsPageInteractionBtnClick?: (
    params: IProductDetailsPageInteractionBtnClickParams,
  ) => void;
  SDProductDetailsPageRelatedMktExp?: (params: any, index: number) => void;
  SDProductDetailsPageRelatedMktClick?: (params: any, index: number) => void;
  SDProductDetailsPageProductPicExp?: (progress: number) => void;
  clientRenderReady: boolean;
  promotionScheduleData?: any;
  isBot?: boolean;
}

const PcGoodsDetail: React.FC<PcGoodsDetailProps> = ({
  detail,
  refreshPageData,
  reviewsData,
  getGlassesFilter,
  addCartLoading,
  frameOnlyLoading,
  selectLensLoading,
  handleShowSizeGuide,
  bottomButtonClick,
  breadcrumbItems,
  SD_TryONBtn_Exp,
  SDProductDetailsPageBtnClick,
  SDProductDetailsPageInteractionBtnClick,
  SDProductDetailsPageRelatedMktExp,
  SDProductDetailsPageRelatedMktClick,
  SDProductDetailsPageProductPicExp,
  clientRenderReady,
  promotionScheduleData,
  isBot = false,
}) => {
  const product = detail?.product;
  const userInfo = useStore((state) => state.userInfo);
  const currency = useStore((state) => state.currency);
  const tryOnAction = useStore((state) => state.handleTryon);
  const waiMode = useStore((state) => state.waiMode);
  const [reviewSectionReady, setReviewSectionReady] = useState(isBot);
  const richText = useMemo(() => {
    const { graphic_details = '' } = detail.product;
    return graphic_details;
  }, [detail.product]);

  useEffect(() => {
    if (!isBot) {
      setReviewSectionReady(true);
    }
  }, [isBot]);

  // 计算priceInfo
  const priceInfo = useMemo(() => {
    // 会员价
    if (
      userInfo?.is_member === 1 &&
      product &&
      product[`price_info_v${userInfo.member_level}`] &&
      product[`discount_percent_v${userInfo.member_level}`] > 0
    ) {
      return genPrice(product[`price_info_v${userInfo.member_level}`], currency.name);
    }
    if (product && product.price_info) {
      return genPrice(product.price_info, currency.name);
    }
    return {};
  }, [product, userInfo, currency]);

  const handleTryOn = useCallback(() => {
    tryOnAction(product);
  }, [product]);

  const showGoodsScene = useMemo(() => {
    return product?.scene_img?.length > 0;
  }, [product?.scene_img]);

  // 渲染活动横幅（scene=2）
  const renderActivityBanners = useMemo(() => {
    if (!product?.commodity_details_label || !Array.isArray(product.commodity_details_label)) {
      return null;
    }

    return product.commodity_details_label
      .filter((item) => item.type === 'activity' && item.scene === 2 && item.status === 1)
      .map((item, index) => (
        <div className="mt-24" key={`activity-banner-${index}`}>
          <ActivityBanner item={item} SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick} />
        </div>
      ));
  }, [product, SDProductDetailsPageBtnClick]);

  const renderCustomerReviews = () => {
    if (isBot) {
      return (
        <CustomerReviewsList
          reviewsData={reviewsData}
          product={product}
          isGlass={product.is_glass === 1}
          isWeb
          SDProductDetailsPageInteractionBtnClick={SDProductDetailsPageInteractionBtnClick}
        />
      );
    }

    if (!reviewSectionReady) {
      return <PCCustomerReviewsLoadingSkeleton />;
    }

    return (
      <CustomerReviewsListClientOnly
        reviewsData={reviewsData}
        product={product}
        isGlass={product.is_glass === 1}
        isWeb
        SDProductDetailsPageInteractionBtnClick={SDProductDetailsPageInteractionBtnClick}
      />
    );
  };

  return (
    <div className="pc-goods-detail min-h-screen bg-white">
      {/* 主要内容区域 - 使用flex布局实现左右分栏 */}
      <div className="flex justify-center items-start" style={{ gap: '2.5vw' }}>
        {/* 左侧 - 图片展示区域 (43vw宽度，sticky定位实现固定效果) */}
        <div
          className="sticky self-start top-[120px] z-4"
          style={{ width: 'calc(38.3vw + 72px + 16px)' }}
        >
          {/* 面包屑导航 */}
          <div className="pt-[.78125vw] pb-[1.67vw]">
            <Breadcrumbs
              items={breadcrumbItems}
              SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
              sx={{
                '& .MuiBreadcrumbs-li p, & .MuiBreadcrumbs-li a span, & .MuiBreadcrumbs-separator':
                  {
                    fontSize: '14px',
                    color: waiMode ? colors.primaryBlack : colors.primaryText,
                  },
                '& .MuiBreadcrumbs-li a': {
                  display: 'block',
                  fontSize: '14px',
                  lineHeight: '1.5',
                },
              }}
            />
          </div>
          <TopCarousel
            productDetail={product}
            showTabBtn={false}
            showTryOn
            isGoodsDetail
            isWeb
            onTryOn={() => {
              handleTryOn();
              SDProductDetailsPageBtnClick?.('TRY ON');
            }}
            SD_TryONBtn_Exp={SD_TryONBtn_Exp}
            SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
          />
        </div>

        {/* 右侧 - 商品信息区域 (固定472px宽度，内容可滚动) */}
        <div className="flex-shrink-0 pt-[calc(2.45vw+21px)]" style={{ width: '472px' }}>
          {/* 商品基本信息 */}
          <div className="mb-8">
            <PcProductInfo
              refreshPageData={refreshPageData}
              product={product}
              getGlassesFilter={getGlassesFilter}
              onShowSizeGuide={handleShowSizeGuide}
              customer={detail.customer}
              clientRenderReady={clientRenderReady}
              reviewsData={reviewsData}
              priceInfo={priceInfo}
              SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
              SDProductDetailsPageRelatedMktExp={SDProductDetailsPageRelatedMktExp}
              SDProductDetailsPageRelatedMktClick={SDProductDetailsPageRelatedMktClick}
              promotionScheduleData={promotionScheduleData}
            />
          </div>

          {/* 购买按钮区域 */}
          <div className="mt-[1.25vw] mb-[.9375vw]">
            <StickyBottomButton
              detail={detail}
              bottomButtonClick={bottomButtonClick}
              addCartLoading={addCartLoading}
              frameOnlyLoading={frameOnlyLoading}
              selectLensLoading={selectLensLoading}
              SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
              getGlassesFilter={getGlassesFilter}
            />
          </div>
          {/* 分期付款 */}
          {!IS_DE && (
            <CycleMsg
              price={priceInfo.sell_price || 0}
              SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
            />
          )}
          {/* 活动横幅（scene=2） */}
          {renderActivityBanners}
          <div className="h-40" />
          {/* 镜片选择组件（仅眼镜产品显示） */}
          {product.is_glass === 1 && (
            <div className="mb-8">
              <LenseAvailable
                detail={product}
                SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
              />
            </div>
          )}

          {/* 产品包含内容 */}
          <div className="mb-8">
            <ProductIncluded
              detail={product}
              className="grid-cols-2"
              SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
            />
          </div>

          {/* 免费礼品（仅眼镜产品显示） */}
          {product.is_glass === 1 && (
            <div>
              <FreeGift
                detail={product}
                defaultOpen={false}
                isWeb
                SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
              />
            </div>
          )}
        </div>
      </div>
      <ProductTab
        detail={product}
        reviewsData={reviewsData}
        isBot={isBot}
        SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
      />
      {/* 商品场景 - 爬虫SSR / 普通用户CSR */}
      {showGoodsScene &&
        (isBot ? (
          <GoodsScene sceneImg={product.scene_img} />
        ) : (
          <GoodsSceneClientOnly sceneImg={product.scene_img} />
        ))}

      {/* 商品参数 - 富文本详情（爬虫SSR / 普通用户CSR） */}
      {!showGoodsScene &&
        richText &&
        product.is_glass === 1 &&
        (isBot ? (
          <ProductRichText
            richText={richText}
            detail={detail.product}
            SDProductDetailsPageProductPicExp={SDProductDetailsPageProductPicExp}
          />
        ) : (
          <ProductRichTextClientOnly
            richText={richText}
            detail={detail.product}
            SDProductDetailsPageProductPicExp={SDProductDetailsPageProductPicExp}
          />
        ))}

      {/* 商品特色介绍 - 爬虫SSR / 普通用户CSR */}
      {product?.feature_introduction?.length > 0 &&
        (isBot ? (
          <GoodsFeatureIntro featureIntroduction={product.feature_introduction} />
        ) : (
          <GoodsFeatureIntroClientOnly featureIntroduction={product.feature_introduction} />
        ))}
      {/* 用户评论 - 爬虫SSR / 普通用户CSR */}
      {reviewsData.all_count > 0 && renderCustomerReviews()}
    </div>
  );
};

export default PcGoodsDetail;
